import { LiveAnnouncer } from "@angular/cdk/a11y";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { Overlay, OverlayConfig, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, ComponentType, TemplatePortal } from "@angular/cdk/portal";
import { ComponentRef, EmbeddedViewRef, inject, Injectable, InjectionToken, Injector, OnDestroy, TemplateRef } from "@angular/core";
import { take, takeUntil } from "rxjs/operators";
import { VDS_TOAST_DATA, VdsToastConfig } from "./toast.config";
import { VdsToastContainerComponent } from "./container/container.component";
import { VdsToastRef } from "./toast.ref";
import { VdsSimpleToastComponent } from "./simple-toast/simple-toast.component";

/** Injection token that can be used to specify default toast. */
export const VDS_TOAST_DEFAULT_OPTIONS = new InjectionToken<VdsToastConfig>("vds-toast-default-options", {
    providedIn: "root",
    factory: VDS_TOAST_DEFAULT_OPTIONS_FACTORY
});

/** @docs-private */
export function VDS_TOAST_DEFAULT_OPTIONS_FACTORY(): VdsToastConfig {
    return new VdsToastConfig();
}

@Injectable({ providedIn: "root" })
export class VdsToast implements OnDestroy {
    private live = inject(LiveAnnouncer);
    private injector = inject(Injector);
    private overlay = inject(Overlay);
    private breakpointObserver = inject(BreakpointObserver);
    private parentToast = inject(VdsToast, { optional: true, skipSelf: true });
    private defaultConfig = inject<VdsToastConfig>(VDS_TOAST_DEFAULT_OPTIONS);

    /**
     * Reference to the current toast in the view *at this level* (in the Angular injector tree).
     * If there is a parent toast service, all operations should delegate to that parent
     * via `_openedToastRef`.
     */
    private _toastRefAtThisLevel: VdsToastRef<any> | null = null;

    /** Reference to the currently opened toast at *any* level. */
    get _openedToastRef(): VdsToastRef<any> | null {
        const parent = this.parentToast;
        return parent ? parent._openedToastRef : this._toastRefAtThisLevel;
    }

    set _openedToastRef(value: VdsToastRef<any> | null) {
        if (this.parentToast) {
            this.parentToast._openedToastRef = value;
        } else {
            this._toastRefAtThisLevel = value;
        }
    }

    /**
     * Creates and dispatches a toast with a custom component for the content, removing any
     * currently opened toasts.
     *
     * @param component Component to be instantiated.
     * @param config Extra configuration for the toast.
     */
    openFromComponent<T>(component: ComponentType<T>, config?: VdsToastConfig): VdsToastRef<T> {
        return this._attach(component, config) as VdsToastRef<T>;
    }

    /**
     * Creates and dispatches a toast with a custom template for the content, removing any
     * currently opened toasts.
     *
     * @param template Template to be instantiated.
     * @param config Extra configuration for the toast.
     */
    openFromTemplate(template: TemplateRef<any>, config?: VdsToastConfig): VdsToastRef<EmbeddedViewRef<any>> {
        return this._attach(template, config);
    }

    /**
     * Opens a toast with a message and an optional action.
     * @param message The message to show in the toast.
     * @param action The label for the toast action.
     * @param config Additional configuration options for the toast.
     */
    open(message: string, action: string | null = "", config?: VdsToastConfig): VdsToastRef<VdsSimpleToastComponent> {
        const _config = { ...this.defaultConfig, ...config };

        // Since the user doesn't have access to the component, we can
        // override the data to pass in our own message and action.
        _config.data = { message, action, type: _config.type };

        if (_config.announcementMessage === message) {
            _config.announcementMessage = undefined;
        }

        return this.openFromComponent(VdsSimpleToastComponent, _config);
    }

    /**
     * Dismisses the currently-visible toast.
     */
    dismiss(): void {
        if (this._openedToastRef) {
            this._openedToastRef.dismiss();
        }
    }

    ngOnDestroy() {
        // Only dismiss the toast at the current level on destroy.
        if (this._toastRefAtThisLevel) {
            this._toastRefAtThisLevel.dismiss();
        }
    }

    /**
     * Attaches the toast container component to the overlay.
     */
    private _attachToastContainer(overlayRef: OverlayRef, config: VdsToastConfig): VdsToastContainerComponent {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = Injector.create({
            parent: userInjector || this.injector,
            providers: [{ provide: VdsToastConfig, useValue: config }]
        });

        const containerPortal = new ComponentPortal(VdsToastContainerComponent, config.viewContainerRef, injector);
        const containerRef: ComponentRef<VdsToastContainerComponent> = overlayRef.attach(containerPortal);
        containerRef.instance.toastConfig = config;
        return containerRef.instance;
    }

    /**
     * Places a new component or a template as the content of the toast container.
     */
    private _attach<T>(content: ComponentType<T> | TemplateRef<T>, userConfig?: VdsToastConfig): VdsToastRef<T | EmbeddedViewRef<any>> {
        const config = {
            ...new VdsToastConfig(),
            ...this.defaultConfig,
            ...userConfig
        };
        const overlayRef = this._createOverlay(config);
        const container = this._attachToastContainer(overlayRef, config);
        const toastRef = new VdsToastRef<T | EmbeddedViewRef<any>>(container, overlayRef);

        if (content instanceof TemplateRef) {
            const portal = new TemplatePortal(content, null!, {
                $implicit: config.data,
                toastRef: toastRef
            } as any);

            toastRef.instance = container.attachTemplatePortal(portal);
        } else {
            const injector = this._createInjector(config, toastRef);
            const portal = new ComponentPortal(content, undefined, injector);
            const contentRef = container.attachComponentPortal<T>(portal);

            // We can't pass this via the injector, because the injector is created earlier.
            toastRef.instance = contentRef.instance;
        }

        // Subscribe to the breakpoint observer and attach the vds-toast--handset class as
        // appropriate. This class is applied to the overlay element because the overlay must expand to
        // fill the width of the screen for full width toasts.
        this.breakpointObserver
            .observe(Breakpoints.Handset)
            .pipe(takeUntil(overlayRef.detachments().pipe(take(1))))
            .subscribe((state) => {
                if (state.matches) {
                    overlayRef.overlayElement.classList.add("vds-toast--handset");
                } else {
                    overlayRef.overlayElement.classList.remove("vds-toast--handset");
                }
            });

        if (config.announcementMessage) {
            container._onAnnounce.subscribe(() => {
                this.live.announce(config.announcementMessage!, config.politeness);
            });
        }

        this._animateToast(toastRef, config);
        this._openedToastRef = toastRef;
        return this._openedToastRef;
    }

    /** Animates the old toast out and the new one in. */
    private _animateToast(toastRef: VdsToastRef<any>, config: VdsToastConfig) {
        // When the toast is dismissed, clear the reference to it.
        toastRef.afterDismissed().subscribe(() => {
            // Clear the toast ref if it hasn't already been replaced by a newer toast.
            if (this._openedToastRef == toastRef) {
                this._openedToastRef = null;
            }

            if (config.announcementMessage) {
                this.live.clear();
            }
        });

        if (this._openedToastRef) {
            // If a toast is already in view, dismiss it and enter the
            // new toast after exit animation is complete.
            this._openedToastRef.afterDismissed().subscribe(() => {
                toastRef.containerInstance.enter();
            });
            this._openedToastRef.dismiss();
        } else {
            // If no toast is in view, enter the new toast.
            toastRef.containerInstance.enter();
        }

        // If a dismiss timeout is provided, set up dismiss based on after the toast is opened.
        if (config.duration && config.duration > 0) {
            toastRef.afterOpened().subscribe(() => toastRef._dismissAfter(config.duration!));
        }

        if (config.announcementMessage) {
            this.live.announce(config.announcementMessage, config.politeness);
        }
    }

    /**
     * Creates a new overlay and places it in the correct location.
     * @param config The user-specified toast config.
     */
    private _createOverlay(config: VdsToastConfig): OverlayRef {
        const overlayConfig = new OverlayConfig();
        overlayConfig.direction = config.direction;

        const positionStrategy = this.overlay.position().global();
        // Set horizontal position.
        const isRtl = config.direction === "rtl";
        const isLeft =
            config.horizontalPosition === "left" ||
            (config.horizontalPosition === "start" && !isRtl) ||
            (config.horizontalPosition === "end" && isRtl);
        const isRight = !isLeft && config.horizontalPosition !== "center";
        if (isLeft) {
            positionStrategy.left("0");
        } else if (isRight) {
            positionStrategy.right("0");
        } else {
            positionStrategy.centerHorizontally();
        }
        // Set horizontal position.
        if (config.verticalPosition === "top") {
            positionStrategy.top("0");
        } else {
            positionStrategy.bottom("0");
        }

        overlayConfig.positionStrategy = positionStrategy;
        return this.overlay.create(overlayConfig);
    }

    /**
     * Creates an injector to be used inside of a toast component.
     * @param config Config that was used to create the toast.
     * @param toastRef Reference to the toast.
     */
    private _createInjector<T>(config: VdsToastConfig, toastRef: VdsToastRef<T>): Injector {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;

        return Injector.create({
            parent: userInjector || this.injector,
            providers: [
                { provide: VdsToastRef, useValue: toastRef },
                { provide: VDS_TOAST_DATA, useValue: config.data }
            ]
        });
    }
}
